<script setup lang="ts">
import { SUPPORTED_MIME_TYPES } from '@/backend/types'
import type { Field } from '@/modules/Project/Fields/types'
import ProjectTableDropzoneDialog from '@/modules/Project/ProjectTableDropzoneDialog.vue'
import type { Property } from '@/modules/Project/Properties/types'
import type { Entity } from '@/modules/Project/types'
import { useProjectStore } from '@/modules/Project/useProjectStore'
import { useUploadToFileField } from '@/modules/Project/useUploadToFileField'
import { useWorkspaceContext } from '@/modules/Workspaces/useWorkspaceContext'
import { invariant, nonNullable } from '@/shared/utils/typeAssertions'
import { useDropzone } from '@/sharedComposables/useDropzone'
import { computed, ref, useTemplateRef } from 'vue'

const props = defineProps<{
  projectId: string
  fileProperties: Property<'file'>[]
}>()

const { workspaceId } = useWorkspaceContext()

const projectStore = useProjectStore()

/** Uploads files to a given file property. */
const onUploadToProperty = (propertyId: string, files: File[]) => {
  const field = getFieldToUploadTo(propertyId)

  uploadToFileField({
    filesToUpload: files,
    entityId: field?.entityId,
    parentEntityId: null,
    propertyId,
    workspaceId: workspaceId.value,
    projectId: props.projectId,
  })

  pendingFiles.value = []
}

const containerRef = useTemplateRef('container')
const uploadToFileField = useUploadToFileField(SUPPORTED_MIME_TYPES)
const pendingFiles = ref<File[]>([])
const { isOverDropZone } = useDropzone(containerRef, {
  onAccepted: (files) => {
    /**
     * If there is more than one file property then we show a dialog to prompt
     * the user to select a file property to upload to.
     */
    if (props.fileProperties.length > 1) {
      pendingFiles.value = files
      return
    }

    const firstFileProperty = nonNullable(props.fileProperties[0])
    onUploadToProperty(firstFileProperty.id, files)
  },
  supportedMimeTypes: SUPPORTED_MIME_TYPES,
})

const entitiesInView = computed(() => projectStore.activeView?.entities?.filter((e) => !!e) || [])
/**
 * Gets the field to which files should be uploaded. This will be the file field
 * for the first empty entity in the view where all subsequent entities are empty.
 * 'Emptiness' is defined as a field that has not been updated by the user or a tool.
 *
 * If the last entity in the view is non-empty then this function will return null
 * and we will create a new entity when dragging files into the dropzone.
 */
function getFieldToUploadTo(propertyId: string): Field<'file'> | null {
  let lastEmptyEntity: Entity | null = null
  const reverseEntities = entitiesInView.value.toReversed()

  for (const entity of reverseEntities) {
    const fields = Array.from(entity.fields.values())

    if (fields.some((f) => f.manualValueUpdatedBy || f.toolValueUpdatedBy)) {
      break
    }

    lastEmptyEntity = entity
  }

  if (!lastEmptyEntity) {
    return null
  }

  const field = lastEmptyEntity?.fields.get(propertyId)
  invariant(field?.type === 'file')

  return field
}
</script>

<template>
  <div
    v-if="fileProperties.length > 0"
    ref="container"
    v-bind="$attrs"
    data-test="project-table-dropzone"
  >
    <div
      v-if="isOverDropZone"
      class="absolute inset-2 flex items-center justify-center rounded-corner-20 bg-background-blue-subtle text-sm-12px-default text-text-selected shadow-focus-ring-primary backdrop-blur-[2px]"
    >
      Upload files to this agent
    </div>
  </div>
  <ProjectTableDropzoneDialog
    :open="pendingFiles.length > 0"
    :files="pendingFiles"
    :properties="fileProperties"
    @cancel="pendingFiles = []"
    @confirm="onUploadToProperty($event, pendingFiles)"
  />
</template>
